import { navigate, type Screen } from '@/app'
import type { ContentCard, HeroItem, Home } from '@/lib/catalog'
import type { ContinueCard } from '@/lib/continue'
import { el, setText } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { trailerAllowed } from '@/lib/ageGate'
import { startResumableHeroTrailer } from '@/lib/trailer'
import { heroSummaryParts, kindLabel } from '@/ui/card'
import { openWebDetail, playWebContent } from '@/browser/detailModal'
import { ICON_FULLSCREEN, ICON_MUTED, ICON_SOUND, iconBox, iconButton } from '@/browser/icons'
import { webArrow, webButton, webCard, webPage, webRow, webScreen } from '@/browser/ui'

/**
 * Ana ekran — brauzer variantı.
 *
 * ⚠ CİHAZDAKI EKRANDAN NƏ İLƏ FƏRQLƏNİR: orada hero sabit 1920×620 sahədir və
 *   sıralar `transform` ilə sürüşür; burada hero pəncərənin hündürlüyünə görə
 *   ölçülür (`58vh`) və sıralar DOĞMA `overflow-x` ilə sürüşür.
 *
 * ⚠⚠ HERO OXLARLA ÇEVRİLİR — TAYMER YOXDUR (sahib tələbi 2026-08-30: «vebtvdə
 *    coverde hero filmlərin sağ sol oxların qoy ki, user çevirə bilsin,
 *    aşağıdakı sectionlar kimi»).
 *
 *    Frontend/CLAUDE.md §4 POZULMUR: orada qadağan olunan AVTOMATİK fırlanmadır
 *    (`setInterval` / öz-özünə axan karusel). Burada taymer YOXDUR — hero yalnız
 *    istifadəçi oxa basanda dəyişir. Saytın öz komponenti də eyni sərhədi çəkir
 *    və eyni sözlərlə izah edir (`apps/web/src/components/HeroCarousel.tsx`:
 *    «Qırmızı xətt 4 POZULMUR: qadağan olunan AVTOMATİK fırlanmadır»); əvvəlki
 *    avtomatik `HeroSlider` məhz o səbəbdən silinmişdi.
 *
 * ⚠ «SEÇİLMİŞLƏR» SIRASI SİLİNDİ: o, yalnız «qalan hero-lar itməsin» deyə vardı
 *   (öz şərhi də bunu deyirdi). İndi hər hero-ya hero-nun ÖZÜNDƏN çatmaq olur,
 *   yəni sıra eyni filmləri İKİNCİ dəfə göstərərdi — üstəlik ilk ox klikindən
 *   sonra sıranın birinci kartı EKRANDAKI hero olardı.
 * ⚠ ŞƏBƏKƏ: keçiddə yalnız BİR yeni treyler başladılır (əvvəlki dayandırılır) və
 *   yalnız istifadəçi istəyəndə — avtomatik dövrənin hər neçə saniyədən bir yeni
 *   mənbə çəkməsi baş vermir. Şəkil ümumiyyətlə yüklənmir.
 * ⚠⚠ HERO-DA TREYLER OYNAYIR, HEÇ BİR ŞƏKİL YOXDUR (sahib qaydası, BR 16.9) — nə
 *    afişa, nə stop-kadr, nə treyler oynamayanda ehtiyat şəkil; treyler yoxdursa
 *    və ya alınmasa yalnız qradiyent fon və mətn qalır. Təfsilat `buildHero`-nun
 *    şərhindədir. POSTERİ GERİ QAYTARMA: 2026-08-10-da hero treyleri üç səthdə
 *    söndürülüb poster qoyulmuşdu, amma 2026-08-13-də sahib brauzer variantı üçün
 *    treyleri geri istədi və 2026-10-02-də «heroda photo göstərmə» qaydasını
 *    təkrar, qəti təsdiqlədi. (Bu sətir 2026-10-02-yə qədər köhnə qərarı yazırdı
 *    və kodla ziddiyyətdə idi — audit tapıntısı.)
 */
/**
 * Ekran hər açılanda göstəriləcək hero-nun indeksi (növbə ilə).
 *
 * Cihaz hədəfindəki `screens/home.ts` → `pickHero` ilə eyni məntiq; izah və səbəb
 * `createWebHomeScreen`-in içindəki şərhdədir. Modul səviyyəsindədir, çünki sayğac
 * ekranın ÖMRÜNDƏN uzun yaşamalıdır — ekran hər dəfə yenidən qurulur.
 */
let heroCursor = 0

function nextHeroIndex(count: number): number {
  if (count <= 0) return 0
  const index = heroCursor % count
  heroCursor = (heroCursor + 1) % count
  return index
}

export function createWebHomeScreen(
  home: Home,
  recommended: ContentCard[],
  continueWatching: ContinueCard[],
  watchlist: ContentCard[],
): Screen {
  const page = webPage()

  const heroes = home.heroes
  /*
    ⚠⚠ EKRAN HƏR AÇILANDA BAŞQA HERO (sahib bildirişi 2026-08-30: «ana səhifə
       açılanda hər dəfə random başqa film görünür heroda. əla. amma menudan menuya
       keçəndə dəyişmir… başqa menuya keçib əsas səhifəyə keçəndə də random olsun»).

       ƏVVƏL: `heroes[0]` — yəni HƏMİŞƏ birinci film. Tam yeniləmədə fərqli görünürdü,
       çünki server hovuzu hər SORĞUDA qarışdırır; menyudan qayıdanda isə sorğu
       getmir (kataloq boot-da bir dəfə çəkilir, `browser/main.ts`) və eyni film
       qalırdı.

       Bu, CİHAZ hədəfindəki `screens/home.ts` → `pickHero` qərarının eynisidir
       (2026-08-03) — orada eyni şikayət artıq həll olunub, brauzer hədəfi isə
       kənarda qalmışdı.

    ⚠ AUTO-ROTATE DEYİL (Frontend/CLAUDE.md qayda 4): taymer yoxdur, indeks yalnız
      ekran YENİDƏN qurulanda irəliləyir.
    ⚠ TƏSADÜF YOX, NÖVBƏ: `Math.random()` beş slaydda tez-tez eyni rəqəmi verir və
      istifadəçi «dəyişmədi» görür. Növbə hər açılışda MÜTLƏQ başqa filmi göstərir.
  */
  const startIndex = nextHeroIndex(heroes.length)
  const hero = heroes.length ? (heroes[startIndex] as HeroItem) : null
  /** Hero treylerini dayandıran funksiya — ekran söküləndə MÜTLƏQ çağırılır. */
  let stopHeroTrailer: (() => void) | null = null
  /** Ekrandakı hero-nun treylerini EYNİ `<video>`-da yenidən başladır (geri qayıdanda). */
  let restartHeroTrailer: (() => (() => void) | null) | null = null
  /** Ekranda olan hero-nun düyünü — ox basılanda MƏHZ o əvəzlənir. */
  let heroNode: HTMLElement | null = null
  let heroIndex = startIndex

  if (hero) {
    /*
      ⚠⚠ OXLAR HERO-NUN İÇİNDƏ DEYİL, QARDAŞIDIR. `.web-hero` `overflow: hidden`
         daşıyır (kadr onun içində miqyaslanır), yəni içəridəki mütləq mövqeli
         düymə kənara çıxan hissəsi ilə KƏSİLƏRDİ. Üstəlik ox hero düyününün
         ÖZÜNÜ əvəz edən keçiddə sağ qalmalıdır — içəridə olsaydı hər keçiddə
         yenidən qurulardı (və fokus itərdi).
      ⚠ QAB `position: relative`-dir (`styles/web.css` → `.web-herowrap`), ona görə
         oxlar hero-nun kənarına görə yerləşir.
    */
    const wrap = el('div', 'web-herowrap')

    const built = buildHero(hero)
    heroNode = built.node
    stopHeroTrailer = built.stop
    restartHeroTrailer = built.restart
    wrap.appendChild(built.node)

    /**
     * Növbəti hero-ya keçir (dövr edir: sonuncudan sonra birinci).
     *
     * ⚠⚠ ƏVVƏLKİ TREYLER MÜTLƏQ DAYANDIRILIR. `startMutedTrailer` hls.js nüsxəsi
     *    və taymerlər qurur; `stop` çağırılmasa köhnə video DOM-dan çıxsa da
     *    dekod etməyə davam edərdi. PERFORMANCE.md §4: eyni anda İKİ video
     *    dekodlaması köhnə TV-də ölçülmüş donma səbəbidir.
     * ⚠ `replaceChild`, `Node.replaceWith` DEYİL: sonuncu Chrome 54+-dır, bu
     *   bundle isə chrome53-ə yığılır (`vite.config.ts`) — orada metod ümumiyyətlə
     *   yoxdur və hero DƏYİŞMƏZDİ (səssiz `TypeError`).
     * ⚠ İKİQAT QALIQ (`((next % total) + total) % total`), tək `%` DEYİL: JS-də
     *   qalıq əməliyyatı İŞARƏNİ SAXLAYIR (`-1 % 6` → `-1`), yəni birinci hero-da
     *   sol ox mənfi indeks verərdi və `heroes[-1]` `undefined` olardı.
     */
    const showHero = (next: number): void => {
      const total = heroes.length
      if (total < 2 || !heroNode || !heroNode.parentNode) return
      const target = ((next % total) + total) % total
      if (target === heroIndex) return

      if (stopHeroTrailer) {
        stopHeroTrailer()
        stopHeroTrailer = null
      }
      const rebuilt = buildHero(heroes[target] as HeroItem)
      heroNode.parentNode.replaceChild(rebuilt.node, heroNode)
      heroNode = rebuilt.node
      stopHeroTrailer = rebuilt.stop
      restartHeroTrailer = rebuilt.restart
      heroIndex = target
    }

    // ⚠ TƏK HERO-DA OX YOXDUR: çevriləcək heç nə olmayanda düymə ölü klikdir.
    if (heroes.length > 1) {
      wrap.appendChild(webArrow(-1, () => showHero(heroIndex - 1)))
      wrap.appendChild(webArrow(1, () => showHero(heroIndex + 1)))
    }

    page.appendChild(wrap)
  }

  const body = el('div')
  body.style.padding = '0 var(--web-pad)'
  page.appendChild(body)

  /*
    ⚠⚠ QISAYOLLAR ARTIQ BURADA DEYİL — HERO-NUN DÜYMƏ SIRASINDADIR, «İzlə»-nin
       SAĞINDA (sahib tələbi 2026-08-11: «Ən çox baxılanlar və Tezliklə buttonu
       filmi izlə button sağında eyni heightdə olsun və border color projenin
       colorundan olsun»). Bax `buildHero`.
    ⚠ HERO YOXDURSA sıra da yoxdur — həmin halda aşağıdakı siyahılar onsuz da
      boşdur (kataloq hazırlanmayıb).
  */
  if (continueWatching.length) {
    const cards = continueWatching.map((item) =>
      webCard(item.card, open, { progress: item.progress }),
    )
    body.appendChild(webRow(T('Davam et'), cards))
  }

  if (recommended.length) {
    body.appendChild(webRow(T('Sənin üçün'), recommended.map((card) => webCard(card, open)), undefined, true))
  }

  if (watchlist.length) {
    body.appendChild(webRow(T('Sonra İzlə'), watchlist.map((card) => webCard(card, open))))
  }

  for (let i = 0; i < home.blocks.length; i++) {
    const block = home.blocks[i]
    if (!block || !block.items.length) continue
    const slug = block.slug
    const title = block.title
    body.appendChild(
      webRow(title, block.items.map((card, index) => webCard(card, open, {
        wide: block.wideItems,
        imageUrl: block.wideItems ? card.backdropUrl || card.posterUrl : card.posterUrl,
        rank: block.showRank ? index + 1 : undefined,
      })), {
        label: T('Hamısı ›'),
        onClick: () => navigate({ name: 'list', kind: 'collection', slug, title }),
      }, block.slug.indexOf('recommended-') === 0),
    )
  }

  if (!hero && !home.blocks.length) {
    body.appendChild(el('div', 'web-empty', T('Kataloq yüklənmədi. Bir azdan yenidən yoxlayın.')))
  }

  // ⚠ İKİ VİDEO PARALEL DEKOD OLUNMAMALIDIR: ekran dəyişəndə (məsələn film
  //   modalından oynadıcıya keçid) hero treyleri dayandırılır.
  // 2026-10-23: ekran «Geri» üçün saxlanılır (`app.ts` → `enableScreenKeepAlive`) — arxaya
  //   düşəndə treyler dayanır, geri qayıdanda EYNİ düyündə yenidən başlayır. Hero düyünü
  //   əvəz edilmir ki, saxlanmış fokus («İzlə» düyməsi) sənəddə qalsın.
  return webScreen(
    page,
    () => {
      if (stopHeroTrailer) stopHeroTrailer()
    },
    {
      park: () => {
        if (stopHeroTrailer) stopHeroTrailer()
        stopHeroTrailer = null
      },
      unpark: () => {
        if (!stopHeroTrailer && restartHeroTrailer) stopHeroTrailer = restartHeroTrailer()
      },
    },
  )
}

/**
 * Kart açılışı — FİLM MODALI (2026-08-13 sahib tələbi: «tv projelər kimi açsın
 * modalı»). Yaş qapısı modalın öz içindədir (`browser/detailModal.ts`), yəni
 * 18+ kartda pəncərə ümumiyyətlə açılmır.
 */
function open(card: ContentCard): void {
  openWebDetail(card)
}

/**
 * Hero.
 *
 * ⚠⚠ TREYLER BİRBAŞA AÇILIR, POSTER YOXDUR (sahib tələbi 2026-08-13: «vebtvdə
 *    hero saytdaki kimi olsun birbaşa trailer açılsın postersiz»). Saytda eyni
 *    qərar 2026-08-12-də verilib (`apps/web` → `HeroTrailer` · `posterMode`):
 *    afişa treylerdən GEC yüklənirdi, yəni «əvvəl şəkil, sonra video» keçidi
 *    həm gecikmə, həm də göz üçün sıçrayış idi.
 *
 * ⚠⚠ EHTİYAT ŞƏKİL DƏ YOXDUR (sahib qaydası, 2026-10-02-də təkrar və qəti: «heroda
 *    photo göstərmə»; BR 16.9). Əvvəl treyler yoxdursa, alınmasa və ya bitsə afişa
 *    yüklənirdi (`showArt`). Ölçüldü (2026-10-02): Vimeo treylerləri arxivdədir və
 *    oynamır, yəni hero-da canlıda məhz afişa görünürdü. Sayt hero-su (d0283a78)
 *    bu halda yalnız qradiyent fon və mətn göstərir — veb TV də «saytdakı kimi».
 *
 * ⚠ 2026-08-10 QƏRARI İLƏ ZİDDİYYƏT DEYİL: onda hero treyleri ÜÇ səthdə
 *   (Android · iOS · veb TV) söndürülmüşdü; indi sahib YALNIZ brauzer variantı
 *   üçün («vebtv», saytla eyni davranış) geri istəyir. Paketlənmiş Tizen/webOS
 *   app-ı toxunulmur — orada köhnə TV-nin dekoderi ana ekranda ikinci videonu
 *   çəkmir (PERFORMANCE.md §4).
 *
 * @returns `stop` — treyleri dayandıran funksiya (ekran söküləndə çağırılır);
 *   `restart` — eyni videoda treyleri yenidən başladır (saxlanmış ekran geri qayıdanda).
 */
function buildHero(hero: HeroItem): {
  node: HTMLElement
  stop: (() => void) | null
  restart: () => (() => void) | null
} {
  const node = el('div', 'web-hero')

  const video = document.createElement('video')
  video.className = 'web-hero__video'
  video.muted = true
  video.setAttribute('playsinline', '')
  node.appendChild(video)

  /*
    ⚠⚠ YAŞ QAYDASI (2026-10-25, kod oxunuşu): hero treyleri HEÇ BİR yoxlamadan keçmirdi —
       18+ hero filmi hər açılışda, girişsiz də, yaşı çatmayana da öz-özünə oynayırdı; «Tam
       ekran» isə onu oynadıcıda səslə açırdı. Sayt eyni qüsuru 2026-10-25-də bağlayıb.
       İndi `trailerAllowed` (saytla eyni qayda): icazə yoxdursa nə treyler, nə səs/tam ekran
       düyməsi var — yalnız qradiyent fon (ehtiyat şəkil yoxdur, sahib qaydası BR 16.9).
       Hökm saxlanmış ekran geri qayıdanda (`restart`) yenidən verilir — yaş sonra yüklənibsə
       treyler o vaxt başlayır.
  */
  const allowedNow = (): boolean => Boolean(hero.trailerUrl) && trailerAllowed(hero.ageLimit)
  let paintControls: (() => void) | null = null
  const startTrailer = (): (() => void) | null => {
    if (paintControls) paintControls()
    if (!hero.trailerUrl || !allowedNow()) return null
    return startResumableHeroTrailer(
      video,
      hero.trailerUrl,
      hero.slug,
      () => {
        video.className = 'web-hero__video is-ready'
      },
      () => {
        // Treyler bitdi/alınmadı/avtoplay bloklandı → qradiyent fon qalır, şəkil yox.
        video.className = 'web-hero__video'
      },
    )
  }
  const stop = startTrailer()

  node.appendChild(el('div', 'web-hero__veil'))
  node.appendChild(el('div', 'web-hero__veil2'))

  const body = el('div', 'web-hero__body')
  // Növ etiketi — saytdakı `.eyebrow` (böyük hərflə, kiçik və solğun).
  body.appendChild(el('p', 'web-hero__eyebrow', kindLabel(hero.kind)))
  body.appendChild(el('h1', 'web-hero__title', hero.title))

  // ⚠ SIRA VƏ MƏZMUN CİHAZ EKRANI İLƏ EYNİLƏŞDİ (sahib maketi 2026-08-11):
  //   reytinq · il · janrlar · müddət · yaş. Əvvəl sıra başqa idi (il · yaş ·
  //   dəqiqə · ★reytinq) və janr ümumiyyətlə yox idi. Ulduz nişanı maketdə
  //   yoxdur — reytinq sadəcə rəqəmdir.
  // ⚠ Müddət TAM formadadır («1 saat 42 dəq»), xam dəqiqə deyil — dörd səthdə
  //   eyni qayda (`ui/card.ts` → `fullRuntime`).
  /*
    ⚠⚠ SAYT MAKETİ (sahib tələbi 2026-08-22: «hero saytdakı kimi olsun tv applar
       kimi yox»). Saytda (`apps/web/src/components/Hero.tsx`) başlığın ÜSTÜNDƏ
       növ etiketi, ALTINDA isə yalnız İKİ çip var: il və yaş həddi. Reytinq,
       janr və müddət sətri TV skinindən gəlirdi — saytda yoxdur, ona görə
       buradan da götürüldü.
    ⚠ Çiplər `web-chip` sinfindədir — həmin sinif kəşf ekranındakı filtrlərlə
      eynidir, yəni yeni stil yaradılmır (dörd səthdə ortaq qayda).
  */
  const chips: string[] = []
  if (hero.year) chips.push(String(hero.year))
  if (hero.ageLimit) chips.push(hero.ageLimit + '+')
  if (chips.length) {
    const row = el('div', 'web-hero__chips')
    for (let i = 0; i < chips.length; i++) {
      row.appendChild(el('span', 'web-chip web-chip--static', chips[i]))
    }
    body.appendChild(row)
  }

  // ⚠ ƏVVƏL SİNOPSİS, SONRA TAQLAYN — cihaz ekranındakı eyni qayda (səbəb orada).
  // ⚠ Kəsiləndə «… daha çox» altdan xətli — cihaz skinindəki eyni qayda (səbəb orada).
  const summary = hero.description || hero.tagline
  if (summary) {
    const parts = heroSummaryParts(summary)
    const tag = el('p', 'web-hero__tag', parts[1] ? parts[0] + '… ' : parts[0])
    if (parts[1]) tag.appendChild(el('span', 'web-hero__more', T('daha çox')))
    body.appendChild(tag)
  }

  /*
    ⚠⚠ DÜYMƏ SIRASI SAYTDAKI KİMİDİR (sahib tələbi 2026-08-22): «İzlə» ·
       «Ətraflı» · səs · tam ekran. ƏVVƏL burada TV skinindən gələn iki qısayol
       («Ən çox baxılanlar», «Tezliklə») vardı — saytın hero-sunda onlar yoxdur.
    ⚠ HEÇ NƏ ƏLÇATMAZ QALMIR: «Ən çox baxılanlar» ana səhifədə serverin öz
      blok sırası kimi onsuz da var, «Tezliklə» isə üst naviqasiyaya köçürüldü
      (`browser/shell.ts` → NAV) — saytda da o, naviqasiya bəndidir.
  */
  const actions = el('div', 'web-hero__actions')
  // ⚠ ETİKET NÖVƏ GÖRƏ — cihaz ekranındakı eyni qayda (`screens/home.ts`).
  const watchLabel = hero.kind === 'movie' ? T('▶  Filmi İzlə') : T('▶  İzlə')
  /*
    2026-10-23: «İzlə» modalın oynatma yolundan keçir (`detailModal.ts` → `playWebContent`):
      yaş qapısı · «Tezliklə» · girişsiz → QR · serialda birinci bölüm · sonluq qatı.
      İzah orada. Detal gözlənilərkən düymə «Açılır…» yazır və təkrar basış udulur —
      `disabled` QOYULMUR, fokuslu düymə sönəndə brauzer fokusu `<body>`-yə atır.
    2026-10-25 (MYTasks 1118): üçüncü arqument `fromHero` — oynadıcıdan çıxış ana ekrana olur,
      modala yox. İzah `detailModal.ts` → `launchPlayer`.
  */
  let busy = false
  const watch = webButton(watchLabel, () => {
    if (busy) return
    playWebContent(hero, (on) => {
      busy = on
      setText(watch, on ? T('Açılır…') : watchLabel)
    }, true)
  })
  actions.appendChild(watch)

  // «Ətraflı» — film modalı (saytda film səhifəsinə aparır; brauzer variantında
  // eyni məlumat modaldadır və kart kliki ilə eyni yoldur).
  const more = webButton(T('Ətraflı'), () => open(hero))
  more.className += ' web-btn--ghost'
  actions.appendChild(more)

  /*
    ⚠ SƏS və TAM EKRAN — YALNIZ TREYLER VARSA (saytdakı eyni şərt): səssiz şəkil
      üzərində bu iki düymə ölüdür.
    ⚠ Səs düyməsi videonun `muted` xassəsini çevirir; etiket dərhal yenilənir ki,
      istifadəçi vəziyyəti görsün.
  */
  if (hero.trailerUrl) {
    /*
      ⚠⚠ YALNIZ İKON, MƏTN YOX (sahib tələbi 2026-08-22: «heroda buttonlar səsi aç
         tam ekran nədir qoymusan onlar həmişə icon olub»). Saytda da bu iki düymə
         `btn--square`-dır — işarə + `aria-label`, etiket yoxdur.
      ⚠ SƏS DÜYMƏSİNDƏ İKİ İŞARƏ VAR və biri gizlidir: hər klikdə `innerHTML`
        yazmaq əvəzinə görünəni dəyişirik (film modalındakı eyni qərar) — SVG
        təzədən parse olunmur, TV brauzerində klik hissi ani qalır.
    */
    const soundOff = iconBox(ICON_MUTED)
    const soundOn = iconBox(ICON_SOUND)
    soundOn.hidden = true
    const sound = iconButton('', T('Səsi aç'), () => {
      video.muted = !video.muted
      soundOff.hidden = !video.muted
      soundOn.hidden = video.muted
      const label = video.muted ? T('Səsi aç') : T('Səsi bağla')
      sound.setAttribute('aria-label', label)
      sound.title = label
    })
    sound.appendChild(soundOff)
    sound.appendChild(soundOn)
    actions.appendChild(sound)

    /*
      ⚠⚠ TAM EKRAN = OYNADICI EKRANI, BRAUZERİN `requestFullscreen`-i YOX
         (sahib tələbi 2026-08-24: «heroda full screen edəndə normal modalda
         açılan full screen olsun, niyə sadəcə ekranı böyüdürsən. modalda
         traileri fullscreen edəndə necə olursa heroda da elə olsun»).

         ƏVVƏL burada `enterFullscreen(node)` idi — hero DOM qutusu brauzer
         tərəfindən böyüdülürdü. Nəticə: ekran doldu, amma İDARƏ QALMADI —
         nə oynat/dayandır, nə sürüşdürmə çubuğu, nə səs, nə keyfiyyət.
         Modalın «Tam ekran» düyməsi (`browser/detailModal.ts` →
         `openTrailerFullscreen`) isə oynadıcı marşrutunu açır və tam dəsti
         verir. İndi hero da EYNİ yolu gedir.
      ⚠ MÖVQE ÖTÜRÜLÜR: treyler hero-da hansı saniyədədirsə oynadıcı ORADAN
        davam edir (modalın eyni qərarı, 2026-08-21).
      ⚠ GERİ: `backToDetail` ana ekrana qayıdır və filmin modalını açır —
        oynadıcıya İKİ yoldan gəlinməsi `app.ts`-də onsuz da nəzərə alınıb.
    */
    const fullscreen = iconButton(ICON_FULLSCREEN, T('Tam ekran'), () => {
      if (!allowedNow()) return
      const at = isFinite(video.currentTime) ? video.currentTime : 0
      navigate({
        name: 'player',
        slug: hero.slug,
        title: hero.title,
        trailerAsset: hero.trailerUrl,
        trailerStartAt: at,
        // ⚠ ÇIXIŞ ANA EKRANADIR, MODALA YOX — izah `app.ts` → `fromHero`.
        fromHero: true,
        ageLimit: hero.ageLimit,
      })
    })
    actions.appendChild(fullscreen)
    // Treyler yaşa görə oynaya bilmirsə səs və tam ekran ölü düymədir — gizlənir.
    paintControls = () => {
      const hidden = !allowedNow()
      sound.hidden = hidden
      fullscreen.hidden = hidden
    }
    paintControls()
  }

  body.appendChild(actions)

  node.appendChild(body)
  return { node, stop, restart: startTrailer }
}
